import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Paperclip,
  RefreshCw,
  RotateCcw,
  Search,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Loader2,
} from "lucide-react";
import {
  useGetCampaignRecipientsQuery,
  useGetMailCampaignQuery,
  useRetryFailedRecipientsMutation,
} from "../api/mailApi";
import { usePermission } from "../../../hooks/usePermission";
import { useAuth } from "../../../context/AuthContexts";
import { isFullHtmlDocument } from "../htmlBody";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "sent", label: "Sent" },
  { key: "failed", label: "Failed" },
  { key: "pending", label: "Pending" },
];

function Stat({
  label,
  value,
  tone = "text-gray-900",
}: {
  label: string;
  value: number | string;
  tone?: string;
}) {
  return (
    <div className="bg-gray-50 rounded-2xl px-5 py-4">
      <p className={`text-2xl font-black leading-none ${tone}`}>{value}</p>
      <p className="text-[9px] font-black uppercase tracking-[0.15em] text-gray-400 mt-2">
        {label}
      </p>
    </div>
  );
}

/**
 * What a sent campaign actually did — per address, with the reason for anything
 * that failed. A send in flight refreshes itself while the queue works through
 * it, so the page settles on its own without the admin reloading.
 */
export default function CampaignDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [banner, setBanner] = useState<string | null>(
    (location.state as any)?.justSent ?? null,
  );

  const { data, isLoading } = useGetMailCampaignQuery(id, { skip: !id });
  const campaign = data?.campaign;
  const inFlight = campaign?.status === "sending";

  const {
    data: report,
    isFetching,
    refetch,
  } = useGetCampaignRecipientsQuery(
    { id, status: status || undefined, search: search || undefined, page },
    { skip: !id, pollingInterval: inFlight ? 5000 : 0 },
  );

  const [retry, { isLoading: retrying }] = useRetryFailedRecipientsMutation();

  // Opening the delivery report needs mail_campaigns.access; acting on it needs
  // mail_campaigns.send. super_admin is bypassed the same way RequirePermission
  // does it, so a role that holds everything is never locked out of a button.
  const { can } = usePermission();
  const { hasRole } = useAuth();
  const canSend = hasRole("super_admin") || can("mail_campaigns.send");

  const rows = report?.recipients?.data ?? [];
  const counts = report?.counts ?? {};

  const onRetry = async () => {
    if (!window.confirm("Requeue every failed copy on this campaign?")) return;
    try {
      const res = await retry(id).unwrap();
      setBanner(res.message);
    } catch (e: any) {
      window.alert(e?.data?.message || "Nothing could be requeued.");
    }
  };

  if (isLoading || !campaign) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-400 p-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  const done = campaign.sent_count + campaign.failed_count;
  const pct = campaign.recipients_count
    ? Math.round((done / campaign.recipients_count) * 100)
    : 0;

  return (
    <div className="space-y-6 max-w-6xl mx-auto p-4 md:p-6 pb-20">
      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-5">
            <button
              onClick={() => navigate("/admin/mail/campaigns")}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all shadow-sm"
            >
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
              <Mail className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-xl font-black text-gray-900 tracking-tight leading-tight">
                {campaign.subject}
              </h1>
              <p className="text-gray-400 text-xs mt-1.5 font-medium">
                Sent by {campaign.creator?.name ?? "—"}
                {campaign.queued_at
                  ? ` · ${new Date(campaign.queued_at).toLocaleString()}`
                  : ""}
                {/* Which sender it left by — the first thing to check when a
                    delivery question comes back weeks later. */}
                {campaign.mailer
                  ? ` · via ${campaign.mailer === "cpanel" ? "Institute webmail" : "Brevo"}`
                  : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => refetch()}
              className="p-3 rounded-xl border bg-white hover:bg-gray-50 transition-all active:scale-95 shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-indigo-500 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>

            {/* Retrying puts real mail back on the wire, so it takes the send
                permission — not the one that opened this report. */}
            {campaign.failed_count > 0 && canSend && (
              <button
                onClick={onRetry}
                disabled={retrying}
                className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-xl px-6 py-3 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-primary disabled:opacity-40 transition-all active:scale-95 shadow-sm"
              >
                {retrying ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RotateCcw className="w-3.5 h-3.5" />
                )}
                Retry {campaign.failed_count} failed
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Recipients" value={campaign.recipients_count} />
          <Stat label="Delivered" value={campaign.sent_count} tone="text-emerald-600" />
          <Stat
            label="Failed"
            value={campaign.failed_count}
            tone={campaign.failed_count ? "text-rose-600" : "text-gray-300"}
          />
          <Stat
            label="Still queued"
            value={Math.max(0, campaign.recipients_count - done)}
            tone={inFlight ? "text-amber-600" : "text-gray-300"}
          />
        </div>

        <div className="h-2 bg-gray-100 rounded-full mt-4 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              campaign.failed_count ? "bg-amber-500" : "bg-emerald-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {inFlight && (
          <p className="text-[10px] text-amber-600 font-bold mt-2 flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Still sending — this page refreshes itself.
          </p>
        )}
      </div>

      {banner && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-3 flex items-center gap-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <p className="text-xs text-emerald-800 font-bold">{banner}</p>
        </div>
      )}

      {/* The message as it went out */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <h2 className="text-[10px] font-black uppercase tracking-[0.15em] text-gray-500">
          Message
        </h2>
        {isFullHtmlDocument(campaign.body) ? (
          // A pasted mailer carries its own stylesheet. Rendered inline it would
          // restyle the page around it, so it is shown in a sandboxed frame —
          // which is also how it reached the recipient.
          <iframe
            title="Message"
            srcDoc={campaign.body}
            sandbox=""
            className="mt-4 w-full h-[560px] bg-white border border-gray-100 rounded-2xl"
          />
        ) : (
          <div
            className="mt-4 text-sm leading-7 text-gray-700 prose-mail-view"
            dangerouslySetInnerHTML={{ __html: campaign.body }}
          />
        )}
        <style>{`
          .prose-mail-view h3 { font-size: 1.05rem; font-weight: 700; margin: 1rem 0 .5rem; }
          .prose-mail-view ul { list-style: disc; padding-left: 1.5rem; margin: .5rem 0; }
          .prose-mail-view ol { list-style: decimal; padding-left: 1.5rem; margin: .5rem 0; }
          .prose-mail-view a { color: #0b2e6b; text-decoration: underline; }
          .prose-mail-view p { margin: 0 0 .75rem; }
        `}</style>

        {(campaign.attachments?.length ?? 0) > 0 && (
          <div className="mt-6 pt-5 border-t border-gray-50 flex flex-wrap gap-2">
            {campaign.attachments!.map((f) => (
              <a
                key={f.id}
                href={f.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2 text-[11px] font-bold text-gray-600 hover:border-gray-900 transition-colors"
              >
                <Paperclip className="w-3 h-3" /> {f.filename}
              </a>
            ))}
          </div>
        )}
      </div>

      {/* Delivery report */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
        <div className="p-6 flex flex-col sm:flex-row gap-3 sm:items-center justify-between border-b border-gray-50">
          <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => {
                  setStatus(tab.key);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-[0.15em] transition-all ${
                  status === tab.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab.label}
                {tab.key && counts[tab.key] ? (
                  <span className="ml-1.5 text-indigo-500">{counts[tab.key]}</span>
                ) : null}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-4 sm:w-72">
            <Search className="w-3.5 h-3.5 text-gray-300" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Name or address…"
              className="w-full py-2.5 text-xs outline-none bg-transparent placeholder:text-gray-300"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                {["Recipient", "Address", "Matched by", "Status", "When"].map((h) => (
                  <th
                    key={h}
                    className="text-left px-6 py-4 text-[9px] font-black uppercase tracking-[0.15em] text-gray-400"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-xs text-gray-300">
                    Nothing to show.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-gray-50">
                  <td className="px-6 py-3 text-xs font-bold text-gray-800">{r.name ?? "—"}</td>
                  <td className="px-6 py-3 text-xs text-gray-500">{r.email}</td>
                  <td className="px-6 py-3 text-[11px] text-gray-400">{r.source ?? "—"}</td>
                  <td className="px-6 py-3">
                    {r.status === "sent" ? (
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-emerald-600">
                        <CheckCircle2 className="w-3 h-3" /> sent
                      </span>
                    ) : r.status === "failed" ? (
                      <span
                        title={r.error_message ?? undefined}
                        className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-rose-600"
                      >
                        <AlertTriangle className="w-3 h-3" /> failed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-amber-600">
                        <Clock className="w-3 h-3" /> queued
                      </span>
                    )}
                    {r.status === "failed" && r.error_message && (
                      <p className="text-[10px] text-rose-400 mt-1 max-w-xs truncate">
                        {r.error_message}
                      </p>
                    )}
                  </td>
                  <td className="px-6 py-3 text-[11px] text-gray-400">
                    {r.sent_at ? new Date(r.sent_at).toLocaleString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {report?.recipients && report.recipients.last_page > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-50">
            <p className="text-[10px] text-gray-400 font-bold">
              Page {report.recipients.current_page} of {report.recipients.last_page} ·{" "}
              {report.recipients.total} recipient(s)
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 disabled:opacity-30 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                disabled={page >= report.recipients.last_page}
                onClick={() => setPage((p) => p + 1)}
                className="px-4 py-2 rounded-xl border text-[10px] font-black uppercase tracking-[0.15em] text-gray-600 disabled:opacity-30 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
